import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    wallet: ['M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4', 'M4 6v12c0 1.1.9 2 2 2h14v-4', 'M18 12a2 2 0 0 0-2 2c0 1.1.1 2 2 2h4v-4h-4z'],
    history: ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5', 'M12 7v5l4 2'],
    export: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    import: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
    card: ['M2 10h20', 'M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z'],
    receipt: ['M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z', 'M16 8h-4', 'M16 12h-4', 'M8 8h.01', 'M8 12h.01'],
    swap: ['M7 10v4h3', 'm7 10 3 3', 'M17 14v-4h-3', 'm17 14-3-3']
};

export default function PettyCash({ user }) {
    const [accounts, setAccounts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // 'add-account', 'add-transaction'
    const [accountNames, setAccountNames] = useState([]);
    const [locations, setLocations] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [staff, setStaff] = useState([]);

    const operatorId = user?.operatorId || 'all';

    useEffect(() => {
        fetchData();
        fetchSettings();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [accRes, transRes, staffRes] = await Promise.all([
                axios.get(`/api/petty-cash/accounts/${operatorId}`),
                axios.get(`/api/petty-cash/transactions/${operatorId}`),
                axios.get(`/api/users/${operatorId}`)
            ]);
            setAccounts(accRes.data);
            setTransactions(transRes.data);
            setStaff(staffRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const fetchSettings = async () => {
        try {
            const res = await axios.get('/api/master-data');
            const data = res.data;
            setAccountNames(data.filter(d => d.category === 'petty-cash-account-names' && d.active));
            setLocations(data.filter(d => d.category === 'petty-cash-locations' && d.active));
            setTransactionTypes(data.filter(d => d.category === 'petty-cash-transaction-types' && d.active));
        } catch (err) { console.error(err); }
    };

    const handleAddAccount = async (formData) => {
        try {
            await axios.post('/api/petty-cash/accounts', { ...formData, operatorId });
            setModal(null);
            fetchData();
        } catch (err) { alert(err.response?.data?.message || 'Error creating account'); }
    };

    const handleAddTransaction = async (formData) => {
        try {
            await axios.post('/api/petty-cash/transactions', { ...formData, operatorId });
            setModal(null);
            fetchData();
        } catch (err) { alert(err.response?.data?.message || 'Error recording transaction'); }
    };

    const totalBalance = accounts.reduce((sum, acc) => sum + acc.currentBalance, 0);
    const totalExpenses = transactions.filter(t => t.type === 'Withdrawal' || t.type === 'Adjustment (Out)').reduce((sum, t) => sum + t.amount, 0);

    return (
        <div className="md-page">
            <header className="md-header">
                <div>
                    <h1 className="md-title">Petty Cash Management</h1>
                    <p className="md-subtitle">Track and manage petty cash funds across locations.</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button className="premium-btn btn-secondary"><Icon d={icons.export} /> Export CSV</button>
                    <button className="premium-btn btn-secondary"><Icon d={icons.import} /> Import Transactions</button>
                    <button className="premium-btn btn-primary-action" onClick={() => setModal('add-account')}>
                        <Icon d={icons.plus} strokeWidth={2.5} /> Add Petty Cash Account
                    </button>
                </div>
            </header>

            <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 30 }}>
                <StatCard label="TOTAL BALANCE" value={`KWD ${totalBalance.toFixed(2)}`} subtext="All active accounts" icon={icons.wallet} color="#4F6EF7" />
                <StatCard label="ACTIVE ACCOUNTS" value={accounts.filter(a => a.status === 'Active').length} subtext="In operation" icon={icons.card} color="#10B981" />
                <StatCard label="TOTAL EXPENSES" value={`KWD ${totalExpenses.toFixed(2)}`} subtext="Paid from petty cash" icon={icons.receipt} color="#EF4444" />
                <StatCard label="TRANSACTIONS" value={transactions.length} subtext="Total recorded" icon={icons.swap} color="#8B5CF6" />
            </div>

            <div className="md-panel" style={{ marginBottom: 30 }}>
                <div className="md-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px' }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Petty Cash Accounts</h3>
                    <div className="md-search-wrap" style={{ margin: 0, width: 250 }}>
                        <Icon d={icons.search} size={14} />
                        <input className="md-input" placeholder="Search accounts..." />
                    </div>
                </div>
                <div className="md-table-wrap">
                    <table className="md-table">
                        <thead>
                            <tr>
                                <th className="md-th">ACCOUNT NAME</th>
                                <th className="md-th">LOCATION</th>
                                <th className="md-th">HANDLER</th>
                                <th className="md-th">CURRENT BALANCE</th>
                                <th className="md-th">FUND LIMIT</th>
                                <th className="md-th">STATUS</th>
                                <th className="md-th">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                                        <Icon d={icons.wallet} size={48} strokeWidth={1} style={{ opacity: 0.2, marginBottom: 15 }} />
                                        <p>No petty cash accounts configured yet.</p>
                                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 10 }}>
                                            <button onClick={() => setModal('add-account')} style={{ color: '#4F6EF7', fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer' }}>Add your first account</button>
                                            <span>•</span>
                                            <button style={{ color: '#4F6EF7', fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer' }}>Configure names & locations first</button>
                                        </div>
                                    </td>
                                </tr>
                            ) : accounts.map(acc => (
                                <tr key={acc._id}>
                                    <td className="md-td"><strong>{acc.accountName}</strong></td>
                                    <td className="md-td">{acc.location}</td>
                                    <td className="md-td">
                                        {acc.handlerId ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{acc.handlerId.firstName[0]}</div>
                                                {acc.handlerId.firstName} {acc.handlerId.lastName}
                                            </div>
                                        ) : 'Unassigned'}
                                    </td>
                                    <td className="md-td"><span style={{ fontWeight: 700, color: acc.currentBalance < acc.fundLimit * 0.2 ? '#EF4444' : '#1e293b' }}>KWD {acc.currentBalance.toFixed(2)}</span></td>
                                    <td className="md-td">KWD {acc.fundLimit.toFixed(2)}</td>
                                    <td className="md-td"><span className={`md-badge ${acc.status === 'Active' ? 'md-badge-active' : 'md-badge-inactive'}`}>{acc.status}</span></td>
                                    <td className="md-td">
                                        <button className="premium-btn btn-secondary" style={{ padding: '4px 12px', fontSize: 11 }} onClick={() => setModal({ type: 'add-transaction', account: acc })}>Record Entry</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="md-panel">
                <div className="md-panel-header" style={{ padding: '20px 24px' }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Recent Transactions</h3>
                </div>
                <div className="md-table-wrap">
                    <table className="md-table">
                        <thead>
                            <tr>
                                <th className="md-th">DATE</th>
                                <th className="md-th">ACCOUNT</th>
                                <th className="md-th">TYPE</th>
                                <th className="md-th">AMOUNT</th>
                                <th className="md-th">BALANCE AFTER</th>
                                <th className="md-th">HANDLER</th>
                                <th className="md-th">DESCRIPTION</th>
                                <th className="md-th">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No transactions recorded yet.</td>
                                </tr>
                            ) : transactions.map(t => (
                                <tr key={t._id}>
                                    <td className="md-td">{new Date(t.date).toLocaleDateString()}</td>
                                    <td className="md-td">{t.accountId?.accountName || '—'}</td>
                                    <td className="md-td">
                                        <span style={{
                                            padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                                            background: t.type === 'Fund Addition' ? '#f0fdf4' : (t.type === 'Withdrawal' ? '#fef2f2' : '#f1f5f9'),
                                            color: t.type === 'Fund Addition' ? '#166534' : (t.type === 'Withdrawal' ? '#991b1b' : '#64748b')
                                        }}>
                                            {t.type}
                                        </span>
                                    </td>
                                    <td className="md-td">
                                        <span style={{ fontWeight: 700, color: (t.type === 'Withdrawal' || t.type === 'Adjustment (Out)') ? '#EF4444' : '#10B981' }}>
                                            {(t.type === 'Withdrawal' || t.type === 'Adjustment (Out)') ? '-' : '+'} KWD {t.amount.toFixed(2)}
                                        </span>
                                    </td>
                                    <td className="md-td">KWD {t.balanceAfter.toFixed(2)}</td>
                                    <td className="md-td">{t.handlerId?.firstName}</td>
                                    <td className="md-td">{t.description}</td>
                                    <td className="md-td"><button className="md-icon-btn"><Icon d={icons.export} size={14} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {modal === 'add-account' && (
                <AddAccountModal
                    names={accountNames}
                    locations={locations}
                    staff={staff}
                    onClose={() => setModal(null)}
                    onSave={handleAddAccount}
                />
            )}

            {modal?.type === 'add-transaction' && (
                <AddTransactionModal
                    account={modal.account}
                    types={transactionTypes}
                    staff={staff}
                    onClose={() => setModal(null)}
                    onSave={handleAddTransaction}
                />
            )}
        </div>
    );
}

function StatCard({ label, value, subtext, icon, color }) {
    return (
        <div className="md-panel" style={{ padding: 24, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', letterSpacing: '0.05em' }}>{label}</div>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}15`, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icon} size={18} />
                    </div>
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', margin: '12px 0 4px' }}>{value}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{subtext}</div>
            </div>
            <div style={{ position: 'absolute', bottom: -10, right: -10, opacity: 0.05, transform: 'rotate(-15deg)' }}>
                <Icon d={icon} size={100} strokeWidth={1} />
            </div>
        </div>
    );
}

function AddAccountModal({ names, locations, staff, onClose, onSave }) {
    const [form, setForm] = useState({ accountNameId: '', locationId: '', handlerId: '', openingBalance: 0, fundLimit: 5000 });

    const handleSubmit = (e) => {
        e.preventDefault();
        const nameObj = names.find(n => n._id === form.accountNameId);
        const locObj = locations.find(l => l._id === form.locationId);
        onSave({
            ...form,
            accountName: nameObj?.name || '',
            location: locObj?.name || ''
        });
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">Add Petty Cash Account</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="md-modal-body" style={{ display: 'grid', gap: 20 }}>
                        <div className="md-form-group">
                            <label className="md-label">ACCOUNT NAME *</label>
                            <select className="md-input" required value={form.accountNameId} onChange={e => setForm({ ...form, accountNameId: e.target.value })}>
                                <option value="">Select Account Name</option>
                                {names.map(n => <option key={n._id} value={n._id}>{n.name}</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">LOCATION *</label>
                            <select className="md-input" required value={form.locationId} onChange={e => setForm({ ...form, locationId: e.target.value })}>
                                <option value="">Select Location</option>
                                {locations.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">HANDLER (STAFF) *</label>
                            <select className="md-input" required value={form.handlerId} onChange={e => setForm({ ...form, handlerId: e.target.value })}>
                                <option value="">Assign to Staff</option>
                                {staff.map(s => <option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>)}
                            </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                            <div className="md-form-group">
                                <label className="md-label">OPENING BALANCE</label>
                                <input type="number" step="0.01" className="md-input" value={form.openingBalance} onChange={e => setForm({ ...form, openingBalance: e.target.value })} />
                            </div>
                            <div className="md-form-group">
                                <label className="md-label">FUND LIMIT</label>
                                <input type="number" step="0.01" className="md-input" value={form.fundLimit} onChange={e => setForm({ ...form, fundLimit: e.target.value })} />
                            </div>
                        </div>
                    </div>
                    <div className="md-modal-footer">
                        <button type="button" className="md-cancel-btn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="md-save-btn">CREATE ACCOUNT</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function AddTransactionModal({ account, types, staff, onClose, onSave }) {
    const [form, setForm] = useState({ accountId: account._id, type: 'Withdrawal', amount: '', handlerId: '', description: '', date: new Date().toISOString().split('T')[0] });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(form);
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">Record Entry: {account.accountName}</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="md-modal-body" style={{ display: 'grid', gap: 20 }}>
                        <div className="md-form-group">
                            <label className="md-label">TRANSACTION TYPE *</label>
                            <select className="md-input" required value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                <option value="Withdrawal">Withdrawal (Expense)</option>
                                <option value="Fund Addition">Fund Addition</option>
                                <option value="Adjustment (In)">Adjustment (In)</option>
                                <option value="Adjustment (Out)">Adjustment (Out)</option>
                            </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                            <div className="md-form-group">
                                <label className="md-label">AMOUNT (KWD) *</label>
                                <input type="number" step="0.01" className="md-input" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} autoFocus />
                            </div>
                            <div className="md-form-group">
                                <label className="md-label">DATE *</label>
                                <input type="date" className="md-input" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                            </div>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">HANDLER (STAFF) *</label>
                            <select className="md-input" required value={form.handlerId} onChange={e => setForm({ ...form, handlerId: e.target.value })}>
                                <option value="">Select Staff</option>
                                {staff.map(s => <option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">DESCRIPTION</label>
                            <input className="md-input" placeholder="e.g. Fuel, Office Supplies..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                    </div>
                    <div className="md-modal-footer">
                        <button type="button" className="md-cancel-btn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="md-save-btn">RECORD TRANSACTION</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
